import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react'
import { useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Button, ErrorState, Loading } from '@/components/ui'
import { loginRequest } from '@/lib/auth'
import { Permissions, useSession } from '@/lib/session'
import { Shell } from '@/app/Shell'
import { AdministrationPage } from '@/features/administration/AdministrationPage'
import { ApiAccessPage } from '@/features/api-access/ApiAccessPage'
import { ApiFeedbackPage } from '@/features/api-access/ApiFeedbackPage'
import { WorkloadPage } from '@/features/delivery/WorkloadPage'
import { DocumentsPage } from '@/features/documents/DocumentsPage'
import { FeedbackPage } from '@/features/feedback/FeedbackPage'
import { PrioritiesPage } from '@/features/priorities/PrioritiesPage'
import { RoadmapPage } from '@/features/reports/RoadmapPage'
import { StatusReportPage } from '@/features/reports/StatusReportPage'
import { ProjectsPage } from '@/features/projects/ProjectsPage'
import { ProjectPage } from '@/features/projects/ProjectPage'
import { TeamsPage } from '@/features/teams/TeamsPage'
import { SharePage } from '@/features/shares/SharePage'
import { SharesPage } from '@/features/shares/SharesPage'
import { WorkbenchPage } from '@/features/workbench/WorkbenchPage'
import { WigBoard } from '@/features/wig/WigBoard'
import { InitiativesPage } from '@/features/initiatives/InitiativesPage'
import { WigPage } from '@/features/wig/WigPage'

function SignIn() {
  const { instance } = useMsal()
  const [failure, setFailure] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  /**
   * Never discard a sign-in rejection.
   *
   * `void instance.loginRedirect(...)` swallowed the error that made sign-in impossible,
   * so the button appeared to do nothing at all. A failure the user cannot see is worse
   * than the failure itself — they have no way to report it and no reason to believe it
   * is not their fault.
   */
  const signIn = async () => {
    setFailure(null)
    setPending(true)
    try {
      await instance.loginRedirect(loginRequest)
    } catch (error) {
      setPending(false)
      setFailure(
        error instanceof Error ? error.message : 'Sign-in could not be started. Please try again.',
      )
    }
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="card w-full max-w-sm p-8 text-center">
        <div
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg font-heading text-2xl font-bold"
          style={{ backgroundColor: 'var(--brand)', color: 'var(--brand-on)' }}
        >
          W
        </div>
        <h1 className="font-heading text-2xl text-fg">Wheeler PMT</h1>
        <p className="mt-1 text-sm text-fg-muted">IT operations project management</p>
        <div className="mt-6">
          <Button variant="primary" onClick={() => void signIn()} disabled={pending}>
            {pending ? 'Redirecting…' : 'Sign in with Microsoft'}
          </Button>
        </div>
        {failure ? (
          <p
            className="mt-4 text-left text-sm"
            role="alert"
            style={{ color: 'var(--status-red)' }}
          >
            {failure}
          </p>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Blocks rendering until permissions are known.
 *
 * Without this the shell would flash every navigation item and then remove the ones the
 * user cannot use, which looks like access being taken away.
 */
function SessionGate() {
  const { isPending, error } = useSession()

  if (isPending) return <Loading label="Signing you in" />
  if (error) {
    return (
      <div className="p-8">
        <ErrorState error={error} />
      </div>
    )
  }
  return <Shell />
}

export function App() {
  /**
   * Share pages are matched **before** the authentication gate.
   *
   * Their whole purpose is to be readable by someone with no account, so they must not be
   * wrapped in `UnauthenticatedTemplate`/`AuthenticatedTemplate` — either one would push
   * the reader at a Microsoft sign-in they cannot complete.
   */
  const isSharePage = window.location.pathname.startsWith('/s/')
  if (isSharePage) {
    return (
      <Routes>
        <Route path="/s/:token" element={<SharePage />} />
      </Routes>
    )
  }

  return (
    <>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <Routes>
          <Route element={<SessionGate />}>
            <Route index element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<ProjectsPage />} />
            {/* One project, full page — a link that can be pasted, bookmarked, or followed
                from the workload view, and what the list opens in a window of its own. */}
            <Route path="/projects/:projectId" element={<ProjectPage />} />
            <Route path="/initiatives" element={<InitiativesPage />} />
            <Route path="/initiatives/:initiativeId" element={<InitiativesPage />} />
            <Route path="/wig" element={<WigPage />} />
            <Route path="/wig/board" element={<WigBoard />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/priorities" element={<PrioritiesPage />} />
            <Route path="/workbench" element={<WorkbenchPage />} />
            <Route path="/workload" element={<WorkloadPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/status-report" element={<StatusReportPage />} />
            <Route path="/shares" element={<SharesPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/administration" element={<AdministrationPage />} />
            <Route path="/api-access" element={<ApiAccessPage />} />
            <Route path="/api-feedback" element={<ApiFeedbackPage />} />
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Route>
        </Routes>
      </AuthenticatedTemplate>
    </>
  )
}

export { Permissions }
